import { FC, useContext } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';

// Contexts
import { LayoutContext } from '../../../core/TenantProvider/contexts';
// Components - Atoms, Molecules, Organisms, Pages
import AEText from '../../atoms/AEText';
// Types
import { HexColour } from '../../../core/types/ThemeConfigTypes';
import { LayoutProps } from '../../../core/types/LayoutProps';
// Utils
import { ReactComponent as NavigationClose } from "./assets/icons/actions-navigation-close-icon.svg";

const UploadedFileDetails = styled(Box)(({ theme }) => {
  const {
    colours: { backgrounds }
  } = theme;

  const COLOUR_BG_BACKGROUND: string = backgrounds.background;

  return {
    display: 'flex',
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: COLOUR_BG_BACKGROUND
  };
});

const FileDetailsContainer = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px'
  };
});

interface FileNameDetailsProps extends LayoutProps { }

const FileNameDetails = styled(AEText)<FileNameDetailsProps>(({ layout }) => {
  const isDesktopLayout: boolean = layout === 'desktop';
  const isTabletLayout: boolean = layout === 'tablet';
  const isTabletLayoutWidth: string = isTabletLayout ? '228px' : '196px';
  
  return {
    maxWidth: isDesktopLayout ? '290px' : isTabletLayoutWidth,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };
});

const FileSizeDetails = styled(AEText)(({ theme }) => {
  const {
    colours: { text }
  } = theme;

  const COLOUR_TEXT_TEXT_WEAKEST: HexColour = text.textWeakest;

  return {
    color: COLOUR_TEXT_TEXT_WEAKEST
  };
});

const IconContainer = styled('span')(({ theme }) => {
  const {
    colours: { text }
  } = theme;

  const COLOUR_TEXT_TEXT: HexColour = text.text;

  return {
    display: 'inline-flex',
    cursor: 'pointer',

    '> svg > path': {
      fill: COLOUR_TEXT_TEXT
    }
  };
});

const NavigationCloseIcon = styled(NavigationClose)(({ theme }) => {
  const {
    colours: {
      text: { textError }
    }
  } = theme;

  const COLOUR_TEXT_TEXT_ERROR: HexColour = textError;

  return {
    marginRight: '6px',
    position: 'relative'
  };
});

interface APUploadedFileDetailsProps {
  fileName: string;
  fileSize: string;
  removeUploadedFileHandler: () => void;
}

const APUploadedFileDetails: FC<APUploadedFileDetailsProps> = (props: APUploadedFileDetailsProps) => {
  const { layout } = useContext(LayoutContext);

  const { fileName: uploadedFileName, fileSize: uploadedFileSize, removeUploadedFileHandler } = props;

  return (
    <UploadedFileDetails>
      <FileDetailsContainer>
        <FileNameDetails
          layout={layout}
          variant="body2"
          type="normal"
          title={uploadedFileName}
          text={uploadedFileName}
        />
        <FileSizeDetails variant="subtitle1" type="normal" text={uploadedFileSize} />
      </FileDetailsContainer>

      <IconContainer data-testid="ae-file-details-close-icon" onClick={removeUploadedFileHandler}>
        <NavigationCloseIcon />
      </IconContainer>
    </UploadedFileDetails>
  );
};

export default APUploadedFileDetails;
